import adminSchema from "./adminSchema.json";

/**
 * @typedef {Object} TextareaFieldDescriptor
 * @property {string} key - Identifies this field. Bound to `<source>[key]` (dot-paths supported,
 *   e.g. "users.hard_limit") unless RfAdmin registers it in `nonFlagsFieldRefs` (e.g.
 *   `ai_design_prompt`, which lives on the model directly).
 * @property {string} [source] - Root object this field reads/writes, keyed into RfAdmin's
 *   `fieldSources` map (e.g. "path_filter"). Defaults to "flags".
 * @property {string} [placeholder] - Placeholder shown in the textarea.
 */

/**
 * @typedef {Object} TextFieldDescriptor
 * @property {string} key - Identifies this field. Bound to `<source>[key]` (dot-paths supported,
 *   e.g. "users.hard_limit") unless RfAdmin registers it in `nonFlagsFieldRefs`.
 * @property {string} [source] - Root object this field reads/writes (see TextareaFieldDescriptor).
 * @property {string} label - Title shown above the input.
 * @property {string} [placeholder] - Placeholder shown in the input.
 * @property {string} [hint] - Supporting hint text shown under the input.
 * @property {import("./conditions").FlagCondition} [visibleIf] - Whether the field renders at all.
 * @property {import("./conditions").FlagCondition} [disabledIf] - Whether the input is disabled.
 * @property {string} [nestedUnder] - Key of a `booleanFields` entry in the same section this field
 *   renders directly beneath, instead of in the section's flat `textFields` list (e.g. Hard/Soft
 *   Limit Users under Users Limits). Independent of `disabledIf` — set both when the field should
 *   also be greyed out while its toggle is off.
 */

/**
 * @typedef {Object} NumberFieldDescriptor
 * @property {string} key - Identifies this field. Bound to `<source>[key]` (dot-paths supported).
 * @property {string} [source] - Root object this field reads/writes (see TextareaFieldDescriptor).
 * @property {string} label - Title shown above the input.
 * @property {string} [hint] - Supporting hint text shown under the input.
 * @property {string} [step] - HTML `step` attribute (e.g. "0.0001" for a fractional rate).
 * @property {import("./conditions").FlagCondition} [visibleIf] - Whether the field renders at all.
 * @property {import("./conditions").FlagCondition} [disabledIf] - Whether the input is disabled.
 * @property {import("./fieldRules").FieldRule[]} [rules] - Declarative live-validation rules.
 */

/**
 * @typedef {Object} SelectFieldDescriptor
 * @property {string} key - Identifies this field. Bound to `<source>[key]` (dot-paths supported)
 *   unless RfAdmin registers it in `nonFlagsFieldRefs` (e.g. `feature_set`, which lives on the
 *   model directly).
 * @property {string} [source] - Root object this field reads/writes (see TextareaFieldDescriptor).
 * @property {string} label - Title shown above the select.
 * @property {string} [placeholder] - Placeholder shown when nothing is selected.
 * @property {boolean} [multiple] - Whether more than one option can be selected.
 * @property {boolean} [search] - Whether the option list is filterable by typing.
 * @property {{ value: string, text: string }[]} options - Selectable options.
 */

/**
 * @typedef {Object} AdminSection
 * @property {string} key - Unique identifier for the section.
 * @property {string} title - Heading rendered as the section's RfDashboardCard title.
 * @property {string} [description] - Optional supporting copy; may contain inline HTML (e.g.
 *   `<code>`) since every entry is static, developer-authored text rather than user input.
 * @property {SelectFieldDescriptor[]} [selectFields] - Single/multi-select dropdown fields.
 * @property {import("./booleanFlags").BooleanFlagDescriptor[]} [booleanFields] - Boolean toggles
 *   rendered as checkboxes.
 * @property {NumberFieldDescriptor[]} [numberFields] - Numeric fields.
 * @property {TextFieldDescriptor[]} [textFields] - Plain-string fields.
 * @property {import("../composables/useListField").ListFieldDescriptor[]} [listFields] - Add/
 *   delete list-of-values or list-of-objects tables (e.g. Traits Whitelist, Coupon Override).
 * @property {TextareaFieldDescriptor[]} [textareaFields] - Multi-line free-text fields.
 */

/**
 * Fully self-contained RfAdmin sections: title, description, and every field a section renders,
 * declared together in one entry (`adminSchema.json`'s `sections`) instead of split across
 * separate copy/flag JSON files. Each entry renders as one RfDashboardCard, looped over rather
 * than hand-written per section — so the schema can move to the backend later without a template
 * change.
 *
 * @type {AdminSection[]}
 */
export const ADMIN_SECTIONS = adminSchema.sections;
